import NextLink from 'next/link';
import { Link, Button, CardActionArea, Tab } from '@rbx/ui';
import React, { Ref } from 'react';
import getNextUrlObject from '../../../utilities/utils/getNextUrlObject';
import useCurrentLocale from '../../../localization/hooks/useCurrentLocale';
import { isAbsolutePath } from '../../../utilities/utils/path';
import throwError from '../../../utilities/error';

type ComponentWithDynamicRouting = typeof Link | typeof Button;

type DynamicallyRoutedProps = {
  href: string;
  ref?: Ref<HTMLAnchorElement>;
  isDocSiteUrl?: boolean;
  skipLocalePrefix?: boolean;
} & Record<string, unknown>;

const withDynamicRouting = (DisplayComponent: ComponentWithDynamicRouting) => {
  // include fine-grained control over how client-side navigation works
  // between in-app pages, especially across our dynamic route segments
  // e.g., ensures '/en-us' goes to a Landing page, while '/studio' does not
  // using next/link. ref:
  // https://nextjs.org/docs/pages/api-reference/components/link#with-url-object

  function DynamicallyRoutedComponent(props: DynamicallyRoutedProps) {
    const { ref, isDocSiteUrl, skipLocalePrefix, href, ...restProps } = props;

    const { isLocalePrefixed, urlLocale } = useCurrentLocale();

    // For non-doc-site URLs (e.g., Creator Hub dashboard links), use a regular anchor tag
    // to bypass Next.js basePath which would incorrectly prefix these URLs
    if (!isDocSiteUrl) {
      return <DisplayComponent ref={ref} href={href} {...restProps} />;
    }

    if (!href) {
      if (process.env.isExport) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <React.Fragment />;
      }
      throwError(`href is required for DynamicallyRouted components. Got '${href}'`);
      // Return the display component without link functionality when href is not provided
      return <DisplayComponent ref={ref} {...restProps} />;
    }

    // next/link cannot handle hash links
    if (href.startsWith('#') || isAbsolutePath(href))
      return <DisplayComponent ref={ref} {...props} />;

    // passHref needed with styled components.
    // legacyBehavior=true is required with passHref.
    // ref: https://nextjs.org/docs/pages/api-reference/components/link#if-the-child-is-a-custom-component-that-wraps-an-a-tag
    const effectiveLocalePrefixed = skipLocalePrefix ? false : isLocalePrefixed;
    let nextUrlObject = getNextUrlObject(href, effectiveLocalePrefixed, urlLocale);

    // Check if we're already on /docs path (doc-site-ssr has basePath: '/docs')
    // If not on /docs, we're on creator-hub which needs the /docs prefix for doc links
    const isDocsSiteHost =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/docs');

    if (!isDocsSiteHost) {
      // Add /docs prefix for doc links when on creator-hub (no basePath)
      if (nextUrlObject instanceof Object) {
        nextUrlObject.pathname = `/docs${nextUrlObject.pathname}`;
      } else {
        nextUrlObject = `/docs${nextUrlObject}`;
      }
    }
    // When on doc-site-ssr, basePath is already '/docs', so no prefix needed
    return (
      // eslint-disable-next-line react/forbid-elements
      <NextLink ref={ref} href={nextUrlObject} passHref legacyBehavior>
        <DisplayComponent {...restProps} />
      </NextLink>
    );
  }

  return DynamicallyRoutedComponent;
};

const DynamicallyRouted = {
  Button: withDynamicRouting(Button),
  Link: withDynamicRouting(Link),
  CardActionArea: withDynamicRouting(CardActionArea),
  Tab: withDynamicRouting(Tab),
};

export default DynamicallyRouted;
